import { State } from '@progress/kendo-data-query';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useCheckbookCategories, {
  EditableColumnCategories,
  adjustBudgetAvailableCategories,
  undeleteAvailableCategories,
} from 'src/assets/static/view_checkbook/useCheckbookCategories';
import ExportExcel, { defaultExcelOption } from 'src/components/ExportExcel';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import ColumnEditorDropDown from '../../components/ColumnEditorDropDown';
import AdjustGLCodeBudget from './AdjustGLCodeBudget';
import DeleteGLCode from './DeleteGLCode';
import ViewCheckbookExcelDropdown, { Category } from './NestedExcelDropdown';
import UnDeleteGLCode from './UnDeleteGLCode';
import { formatCheckbookExcelData } from './index';
import AddGlCode from './period_view/AddGlCode';
import BulkManageAttributes from './period_view/BulkManageAttributes';
import ClearFilters from './period_view/ClearFilters';

const checkbookPeriodOptions: Category[] = [
  {
    content: 'Include Departments',
    id: 1,
    items: [
      { content: 'Current Page', id: 2 },
      { content: 'All Pages', id: 3 },
    ],
    parent: true,
  },
  {
    content: 'Exclude Departments',
    id: 4,
    items: [
      { content: 'Current Page', id: 5 },
      { content: 'All Pages', id: 6 },
    ],
    parent: true,
  },
];

interface GridToolbarProps {
  excelExport: (data: any[], isIncluded?: boolean) => void;
  rows: GLCodePeriodBudgetResponse[];
  setLoading: SetState<boolean>;
  setState: SetState<State>;
  allPageData: GLCodePeriodBudgetResponse[];
}

const ViewCheckbookPeriodTableToolbar: FC<GridToolbarProps> = ({
  excelExport,
  rows,
  setLoading,
  setState,
  allPageData,
}) => {
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const {
    setGlCodePeriodBudget,
    hasEditGLCodePermission,
    hasManageBudgetPermission,
    selectedPeriodDepartment,
    selectedCompany,
    isEditMode,
    confirmBeforeProceeding,
    isSelectedPeriodClosed,
  } = useViewCheckbook();

  const {
    dropdownPeriodFilter,
    setDropdownPeriodFilter,
    editableColumnDropdownData,
    setEditableColumnDropdownData,
    selectedRows,
  } = usePeriodView();

  const categories = useCheckbookCategories();
  const { companyName } = selectedCompany;

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setLoading(true);
        excelExport(rows.map((row) => ({ ...row, companyName })));
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption.value === 2) {
        setLoading(true);
        excelExport(allPageData.map((row) => ({ ...row, companyName })));
        setExcelOption(defaultExcelOption);
        setLoading(false);
      }
    },
    [allPageData, companyName, excelExport, rows, setLoading]
  );

  const handleTreeDropdownExcelChange = useCallback(
    (selectedOption: number) => {
      if (selectedOption === 2) {
        setLoading(true);
        const formattedData = formatCheckbookExcelData(
          rows.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 3) {
        setLoading(true);
        const formattedData = formatCheckbookExcelData(
          allPageData.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 5) {
        setLoading(true);
        excelExport(rows.map((row) => ({ ...row, companyName })));
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 6) {
        setLoading(true);
        excelExport(allPageData.map((row) => ({ ...row, companyName })));
        setExcelOption(defaultExcelOption);
        setLoading(false);
      }
    },
    [allPageData, companyName, excelExport, rows, setLoading]
  );

  const handleCategoryChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e;
      confirmBeforeProceeding(() => {
        setGlCodePeriodBudget((periods) =>
          periods.map((glCode) => ({ ...glCode, selected: false }))
        );
        setDropdownPeriodFilter(value);
        setState((prev) => {
          if (modifyRequest(prev).filters.length) {
            toast.info('Preset filter applied. User-applied filters cleared.');
          }
          return {
            ...prev,
            skip: 0,
            filter: {
              logic: 'and',
              filters: [],
            },
          };
        });
      });
    },
    [confirmBeforeProceeding, setDropdownPeriodFilter, setGlCodePeriodBudget, setState]
  );
  const handleEditColumnChange = useCallback(
    (dataItem: EditableColumnCategories[]) => {
      setEditableColumnDropdownData(dataItem);
    },
    [setEditableColumnDropdownData]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-1">
        <ClearFilters />
        {!isSelectedPeriodClosed && (
          <>
            {hasEditGLCodePermission && (
              <>
                <AddGlCode />
                <DeleteGLCode selectedRows={selectedRows} />
                {undeleteAvailableCategories.includes(dropdownPeriodFilter.value) && (
                  <UnDeleteGLCode selectedRows={selectedRows} />
                )}
              </>
            )}
            {isEditMode &&
              hasManageBudgetPermission &&
              adjustBudgetAvailableCategories.includes(dropdownPeriodFilter.value) && (
                <AdjustGLCodeBudget />
              )}
            <BulkManageAttributes />
          </>
        )}
      </div>

      <div className="flex flex-row gap-2 flex-wrap">
        {selectedPeriodDepartment.departmentName === 'All Departments' ? (
          <ViewCheckbookExcelDropdown
            options={checkbookPeriodOptions}
            handleExcelChange={handleTreeDropdownExcelChange}
          />
        ) : (
          <ExportExcel excelOption={excelOption} onExcelChanged={handleExcelChange} width={200} />
        )}

        <DropDownList
          id="view-drop-down-list"
          style={{ width: 200 }}
          data={categories}
          textField="label"
          dataItemKey="value"
          value={dropdownPeriodFilter}
          onChange={handleCategoryChange}
        />
        <ColumnEditorDropDown onChange={handleEditColumnChange} data={editableColumnDropdownData} />
      </div>
    </div>
  );
};
export default ViewCheckbookPeriodTableToolbar;
